/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
// disabled because: let the compiler infer the types
import type { ConversationHistory } from 'lib-shared-types-api';
import { createSelector } from 'reselect';

import { AppState } from 'bootstrap.store';

import { ElevioLanguage, SelectedLanguageMode } from '../types/elevio.actions';
import { elevioApi } from '../utils/apis/graphql/api';
import { extractConversationTextFromMessages } from '../utils/messages';

import { ElevioCombinedLanguageStore, SelectedLanguage } from './suggestion';

/**
 * Returns the selected articleId for Elevio suggestions.
 *
 * Returns `null` if there is no:
 *
 * - ConversationId
 * - ConversationState
 * - ArticleIds
 */
const articleId = (store: AppState): string | null => {
  const conversationId = store.conversation.metaConversation.conversation.id;
  if (!conversationId) return null;

  const conversationState =
    store.elevio.suggestions.conversationStore[conversationId];
  if (!conversationState) return null;

  const articleIds = conversationState.articleIds;
  if (!articleIds) return null;

  return articleIds.length > 0 ? articleIds[articleIds.length - 1] : null;
};

/** Elevio auth state */
const authState = (store: AppState) => store.elevio.suggestions.authState;
/** All suggestion data for all conversations */
const conversationStore = (store: AppState) =>
  store.elevio.suggestions.conversationStore;
/** All search data for all conversations */
const searchStore = (store: AppState) => store.elevio.search.searchStore;

const resolutionSelectors = {
  articleDropdownOpen: (store: AppState) =>
    store.elevio.resolution.articleDropdownOpen,
  articleSearchTerm: (store: AppState) =>
    store.elevio.resolution.articleSearchTerm,
  articleSolved: (store: AppState) => store.elevio.resolution.articleSolved,
  draft: (store: AppState) => store.elevio.resolution.draft,
  previousState: (store: AppState) => store.elevio.resolution.previousState,
  selectedArticleId: (store: AppState) =>
    store.elevio.resolution.selectedArticleId,
  articleSearchInputValue: (store: AppState) =>
    store.elevio.resolution.articleSearchInputValue,
  selectedArticleTitle: (store: AppState) =>
    store.elevio.resolution.selectedArticleTitle,
  stage: (store: AppState) => store.elevio.resolution.stage,
};

/**
 * There seems to be no way to find a standard type for this data shape, in
 * particular the data.data.
 *
 * Using conversationsApi.endpoints.readConversation.initiate({csid:
 * conversationId}) in lieu of `conversation =
 * store.api.queries[conversationKey];` was not helpful as the return type did
 * not map directly to what's in the store.
 */
type StoreConversation = {
  data: {
    data: { history: ConversationHistory };
  };
};

/** Selects conversation Tag[] */
const tagsSelector = createSelector(
  (store: AppState) => {
    const conversationId = store.conversation.metaConversation.conversation.id;
    if (!conversationId) return [];

    const conversationKey = `readConversation({"csid":${conversationId}})`;
    const conversation = store.api?.queries[conversationKey];

    const conversationTags =
      (conversation as unknown as StoreConversation)?.data?.data?.history
        .main_conversation.tags || [];
    const legacyTags =
      store.conversation.metaConversation.conversation.tags || [];

    const tags =
      !conversation || !conversationTags ? legacyTags : conversationTags;

    return tags;
  },
  (tags) => tags,
);
const messagesSelector = createSelector(
  (store: AppState) => store.conversation.metaConversation.messages,
  (messages) => messages,
);
const conversationSelectors = {
  id: (store: AppState) =>
    store.conversation.metaConversation.conversation.id || null,
  status: (store: AppState) =>
    store.conversation.metaConversation.conversation.status,
  tags: tagsSelector,
  /** Selects tags as a string[] */
  tagNames: createSelector(tagsSelector, (tags) => tags.map((tag) => tag.name)),
  subject: (store: AppState) =>
    store.conversation.metaConversation.conversation.attributes?.subject ?? '',
  /** List of messages in a conversation */
  messages: messagesSelector,
  /** List of messages in a conversation as a single piece of text */
  text: createSelector(messagesSelector, (messages) =>
    extractConversationTextFromMessages(messages),
  ),
  /** Are tags loaded */
  tagsLoaded: (store: AppState) => store.conversation.asyncStatus.tags,
  /** The current agents id */
  agentId: (store: AppState) =>
    store.conversation.metaConversation.conversation.assignment?.agent_id ||
    null,
};

/** List of ArticleSuggestion[] for the current conversation */
const suggestionsForCurrentConversation = createSelector(
  conversationSelectors.id,
  conversationStore,
  (conversationId, conversationState) => {
    if (!conversationId) return [];
    return conversationState[conversationId]?.suggestions;
  },
);
/** Conversation state for the current conversastion */
const conversationStoreForCurrentConversation = createSelector(
  conversationSelectors.id,
  conversationStore,
  (conversationId, conversationState) => {
    if (!conversationId) return;
    return conversationState[conversationId];
  },
);
/** List of article suggestion id's for the current conversation */
const suggestedArticleIds = createSelector(
  suggestionsForCurrentConversation,
  (suggestions) => {
    return suggestions.map((suggestion) => suggestion.id);
  },
);
/** Suggestions are fetching */
const suggestionsFetching = (store: AppState) =>
  store.elevio.suggestions.fetching;
/** There was an error fetching suggestions */
const suggestionErrorMessage = (store: AppState) =>
  store.elevio.suggestions.errorMessage || undefined;
const unableToFindTaggedSuggestions = createSelector(
  conversationSelectors.id,
  conversationStore,
  (conversationId, conversationState) => {
    if (!conversationId) return [];
    return conversationState[conversationId].unableToFindTaggedSuggestions;
  },
);

const getDetectedLanguageId =
  (conversationId: number | null) => (store: AppState) =>
    conversationId
      ? store.elevio.suggestions.languages.detectedLanguages[conversationId]
      : null;
/** The default language selected in Elevio at present */
function getDefaultLanguageSelector(store: AppState): SelectedLanguage {
  const defaultLanguage: SelectedLanguage = {
    ...store.elevio.suggestions.languages.defaultLanguage,
    mode: 'defaultLanguage',
  };
  return defaultLanguage;
}
/** The languages available in Elevio at present */
function getAvailableLanguages(store: AppState) {
  const availableLanguages =
    store.elevio.suggestions.languages.availableLanguages.results || [];
  const availableLanguageIds: string[] = availableLanguages.map(
    (language) => language.id,
  );
  return { availableLanguages, availableLanguageIds };
}
/**
 * Maps a detected language, if available, to a matching Elevio language, if
 * available.
 */
function getAutoDetectedLanguageSelector(
  store: AppState,
): SelectedLanguage | undefined {
  const conversationId = conversationSelectors.id(store);
  const detectedLanguageId = getDetectedLanguageId(conversationId)(store);
  const { availableLanguages, availableLanguageIds } =
    getAvailableLanguages(store);
  const autoDetectedLanguage: SelectedLanguage | undefined =
    !!detectedLanguageId && availableLanguageIds.includes(detectedLanguageId)
      ? {
          ...(availableLanguages.find(
            (lang) => lang.id === detectedLanguageId,
          ) as ElevioLanguage),
          mode: 'autoDetectedLanguage',
        }
      : undefined;
  return autoDetectedLanguage;
}
/**
 * Takes the stored language id and checks if it matches against any of the
 * currently available languages in Elevio. It's possible that a language can be
 * deactivated, so this helps cater for that situation.
 */
function getMatchedLanguageSelector(store: AppState) {
  const { availableLanguages } = getAvailableLanguages(store);
  const storedSelectedLanguageId: SelectedLanguageMode | string =
    store.settings.personal.elevioSelectedLanguage; // it could be an empty string in the store
  const matchedAvailableLanguage: ElevioLanguage | undefined =
    availableLanguages.find((lang) => {
      return lang.id === storedSelectedLanguageId;
    });
  const matchedLanguage: SelectedLanguage | undefined =
    matchedAvailableLanguage && {
      mode: 'userSelectedLanguage',
      id: matchedAvailableLanguage.id,
      nativeName: matchedAvailableLanguage.nativeName,
    };
  return matchedLanguage;
}
function languagesSelector(store: AppState): ElevioCombinedLanguageStore {
  const defaultLanguage = getDefaultLanguageSelector(store);
  const autoDetectedLanguage = getAutoDetectedLanguageSelector(store);
  const matchedLanguage = getMatchedLanguageSelector(store);

  /**
   * Could be an empty string in the store, or it could be an invalid
   * 'deactivated' language id that is no longer active in Elevio.
   */
  const persistedElevioLanguageValue: SelectedLanguageMode =
    store.settings.personal.elevioSelectedLanguage;
  const storedSelectedLanguage: SelectedLanguage =
    matchedLanguage || autoDetectedLanguage || defaultLanguage;

  const languageStore: ElevioCombinedLanguageStore = {
    ...store.elevio.suggestions.languages,
    selectedLanguage: storedSelectedLanguage,
    selectedLanguageId: storedSelectedLanguage.id,
    selectedLanguageMode: persistedElevioLanguageValue,
  };
  return languageStore;
}
/** Required so that changes to languageId are picked up by useAppSelector */
const selectedLanguageId = createSelector(
  languagesSelector,
  (lang) => lang.selectedLanguage.id || '',
);
const suggestionSelectors = {
  authState,
  /** Another name for Elevio workspace ID */
  companyUid: (store: AppState) => store.elevio.suggestions.companyUid,
  token: (store: AppState) => store.elevio.suggestions.token,
  subscriptionStatus: (store: AppState) =>
    store.elevio.suggestions.subscriptionStatus,
  conversationStore: conversationStore,
  isFetching: suggestionsFetching,
  suggestedArticleIds,
  suggestionsForCurrentConversation,
  conversationStoreForCurrentConversation,
  errorMessage: suggestionErrorMessage,
  unableToFindTaggedSuggestions,
  languages: languagesSelector,
  selectedLanguageId,
  defaultLanguage: (store: AppState) =>
    store.elevio.suggestions.languages.defaultLanguage,
  getDetectedLanguage: getDetectedLanguageId,
};

const searchSelectors = {
  searchStore,
  /** Input value in the UI */
  searchInputValue: (store: AppState) => store.elevio.search.searchInputValue,
  /**
   * Selects an Elevio searchterm for a given conversationId. If the
   * conversation does not exist, returns ''.
   */
  searchTerm: createSelector(
    conversationSelectors.id,
    searchStore,
    (id, store) => {
      return id ? (store[id] ? store[id].searchTerm : '') : '';
    },
  ),
};
const userSelectors = {
  /** Agents email */
  email: (store: AppState) => store.elevio.suggestions.userEmail,
  /** KCS settings for the user - returns KcsSettingsQuery */
  kcsSettings: (store: AppState) =>
    elevioApi.grappa.endpoints.kcsSettings.select({})(store),
};
const smartReplySelectors = {
  data: (store: AppState) => store.elevio.aiResponse.data,
  loading: (store: AppState) => store.elevio.aiResponse.loading,
  tabStatus: (store: AppState) => store.elevio.aiResponse.tabStatus,
};
export const elevioSelectors = {
  articleFeedback: (store: AppState) => store.elevio.feedback,
  articleId,
  search: searchSelectors,
  suggestion: suggestionSelectors,
  resolution: resolutionSelectors,
  conversation: conversationSelectors,
  user: userSelectors,
  smartReply: smartReplySelectors,
};
